import {useStore} from '@/store';
import {Commit} from 'vuex';
import {APP_MODULE} from '@/store/state';
import {
  IDateDescription,
  ISolution,
  UserActivityCategoryEnum,
  getSolutionDates,
} from '@mobi-components/global-solution';
import {formatDate, getListOfSolutionDays} from '@/store/state/utils';
import {getDateActivities} from '@/views/amex/components/explore-page/exp-list/utils';

const MODULE_NAME = 'ITINERARY_LIST';

const Getters = {
  GET: `${MODULE_NAME}/GET`,
  GET_SCHEDULE: `${MODULE_NAME}/GET_SCHEDULE`,
};

const Actions = {
  UPDATE: `${MODULE_NAME}/UPDATE`,
};

const Mutations = {
  SET: `${MODULE_NAME}/SET`,
};

const store = useStore();

export interface ContextSchedule {
  context: string;
  schedule: Map<string, IDateDescription>;
}

export interface ItineraryListState {
  schedule: ContextSchedule[];
}

export const ItineraryListModule = {
  getDayById: (id: string, context: string): IDateDescription | null => {
    const schedules = store.getters[`${APP_MODULE}/${Getters.GET_SCHEDULE}`] as ContextSchedule[];
    const contextSchedule = schedules.find((x) => x.context === context);
    const day = contextSchedule ? contextSchedule.schedule.get(id) : null;
    return day || null;
  },

  getSolutionDays(context: string): IDateDescription[] {
    const schedules = store.getters[`${APP_MODULE}/${Getters.GET_SCHEDULE}`] as ContextSchedule[];
    const contextSchedule = schedules.find((x) => x.context === context);
    const days = contextSchedule ? [...contextSchedule.schedule.values()] : [];
    const includedDays = new Set();
    return days.filter((x) => {
      if (includedDays.has(x.date)) {
        return false;
      }
      includedDays.add(x.date);
      return true;
    });
  },

  getNumberOfNights(solution: ISolution | null, context: string): number {
    if (!solution) {
      return 0;
    }

    const contextDays = getListOfSolutionDays(solution, context);
    return contextDays.length - 1;
  },

  getDateActivitiesIds(context: string, date: Date): string[] {
    const schedules = store.getters[`${APP_MODULE}/${Getters.GET_SCHEDULE}`] as ContextSchedule[];
    const contextSchedule = schedules.find((x) => x.context === context);
    return contextSchedule
      ? [...contextSchedule.schedule.entries()]
          .filter(([_, day]) => day.date.valueOf() === date.valueOf())
          .map(([id]) => id)
          .filter(Boolean)
      : [];
  },

  update: (solution: ISolution | null) => {
    // todo context is missing in activity.spec
    const contexts: string[] = solution
      ? // @ts-ignore TODO: fix this.
        [...new Set(solution.activities.map((x) => x.spec?.context).filter(Boolean))]
      : [];

    const schedule = contexts.reduce((result: ContextSchedule[], context) => {
      const contextSchedule = {
        context,
        schedule: new Map(),
      };
      const contextDays = getListOfSolutionDays(solution, context);
      const datesWithDescription = getSolutionDates(contextDays).map((x, index) => ({
        ...x,
        label: `Day ${index + 1}`,
        description: formatDate(x.date),
      }));

      datesWithDescription.forEach((date: IDateDescription) => {
        const allActivities = getDateActivities(solution, date.date).filter(
          (x) =>
            // @ts-ignore TODO: fix this.
            !context || x.activity.spec?.context === context || x.activity.category === UserActivityCategoryEnum.FLIGHT,
        );

        const flights = allActivities.filter((x) => x.activity.category === UserActivityCategoryEnum.FLIGHT);

        const activities = allActivities.filter((x) => {
          const isRecommendation = x.activity.spec?.curated || false;
          // if (x.activity.name === 'Cloud Forest' && x.activity.spec?.curated) {
          //   debugger;
          //   return false;
          // }
          return (
            [UserActivityCategoryEnum.DINING, UserActivityCategoryEnum.EXPERIENCE].includes(x.activity.category) &&
            !isRecommendation
          );
        });

        const stays = allActivities.filter((x) => {
          const isRecommendation = x.activity.spec?.curated || false;
          return [UserActivityCategoryEnum.ACCOMMODATION].includes(x.activity.category) && !isRecommendation;
        });

        // const recommendations = allActivities.filter((x) => {
        //   const isRecommendation = x.activity.spec?.curated;
        //   const isPoi = x.activity.source?.content;
        //   return (
        //     [UserActivityCategoryEnum.DINING, UserActivityCategoryEnum.EXPERIENCE].includes(x.activity.category) &&
        //     isRecommendation &&
        //     isPoi
        //   );
        // });
        const flightIds = flights.map((x) => x.activity.id);
        const activityIds = activities.map((x) => x.activity.id);
        const stayIds = stays.map((x) => x.activity.id);
        // const recommendationIds = recommendations.map((x) => x.activity.id);
        // const poiIds = recommendations.map((x) => x.activity.source?.content?.id || '');
        const activityPoiIds = activities.map((x) => x.activity.source?.content?.id || '');
        const allIdsForADay = [
          ...flightIds,
          ...activityIds,
          ...stayIds,
          // ...recommendationIds,
          // ...poiIds,
          ...activityPoiIds,
        ];
        allIdsForADay.forEach((id) => {
          contextSchedule.schedule.set(id, date);
        });
      });

      result.push(contextSchedule);
      return result;
    }, []);

    return store.dispatch(`${APP_MODULE}/${Actions.UPDATE}`, {
      schedule,
    });
  },

  state: {schedule: []},

  getters: {
    [Getters.GET_SCHEDULE]: (state: ItineraryListState) => {
      return state.schedule;
    },
  },

  actions: {
    [Actions.UPDATE]: async ({commit}: {commit: Commit}, payload: ItineraryListState) => {
      commit(Mutations.SET, payload);
    },
  },
  mutations: {
    [Mutations.SET]: (state: ItineraryListState, payload: ItineraryListState) => {
      state.schedule = payload.schedule;
    },
  },
};
